import type { MutableRefObject } from "react";
import { useDevAgentStore } from "@/stores/devAgentStore";
import { normalizeAssistantContent, normalizeStreamingDelta } from "../normalizeAssistantContent";
import { normalizeSseEventType } from "./sseConstants";
import { S } from "./sseFeedHelpers";
import { sseEventHandlers, armSilenceTimer } from "./sseEventHandlers";
import { sseState } from "./sseMutableState";

interface ProcessSseFrameParams {
  targetUuid: string;
  eventId: string;
  eventType: string;
  eventData: string;
  processedIdsRef: MutableRefObject<Set<string>>;
  lastEventIdRef: MutableRefObject<string>;
  connectedUuidRef: MutableRefObject<string | null>;
  refreshTicketWithRecovery: (targetUuid: string) => Promise<string>;
  reconnect: (targetUuid: string) => void;
}

export type SseFrameAction = "continue" | "reconnect" | "close";

export async function processSseFrame({
  targetUuid,
  eventId,
  eventType,
  eventData,
  processedIdsRef,
  lastEventIdRef,
  connectedUuidRef,
  refreshTicketWithRecovery,
  reconnect,
}: ProcessSseFrameParams): Promise<SseFrameAction> {
  if (useDevAgentStore.getState().sessionUuid !== targetUuid) {
    return "continue";
  }

  if (eventId) {
    if (processedIdsRef.current.has(eventId)) {
      return "continue";
    }
    processedIdsRef.current.add(eventId);
    if (processedIdsRef.current.size > 500) {
      const arr = [...processedIdsRef.current];
      processedIdsRef.current = new Set(arr.slice(-300));
    }
    const curId = parseInt(eventId, 10);
    if (!isNaN(curId) && sseState.lastEventId > 0 && curId > sseState.lastEventId + 1) {
      sseState.gapsDetected += curId - sseState.lastEventId - 1;
    }
    if (!isNaN(curId)) sseState.lastEventId = curId;
    lastEventIdRef.current = eventId;
  }

  if (eventType === "heartbeat" || eventType === "timeout") {
    return "continue";
  }

  if (eventType === "unauthorized") {
    console.warn("[SSE] unauthorized — refreshing ticket");
    refreshTicketWithRecovery(targetUuid).then(async (newTicket) => {
      if (!newTicket) {
        const { clearFrontendSessionAndRedirectToLogin } = await import("@/dataProvider/axios");
        clearFrontendSessionAndRedirectToLogin();
        return;
      }
      useDevAgentStore.getState().setSseTicket(newTicket);
      connectedUuidRef.current = null;
      reconnect(targetUuid);
    }).catch(async () => {
      const { clearFrontendSessionAndRedirectToLogin } = await import("@/dataProvider/axios");
      clearFrontendSessionAndRedirectToLogin();
    });
    return "reconnect";
  }

  try {
    sseState.lastEventAt = Date.now();
    sseState.eventsReceived++;
    armSilenceTimer();
    const p = JSON.parse(eventData);
    if (process.env.NODE_ENV === "development") {
      console.debug("[SSE]", eventType, p?.type || "", p);
    }

    if (eventType === "chat_message") {
      const meta = p.metadata || p.metadata_ || {};
      const toolCalls = meta.tool_calls || [];
      const messageUuid = p.uuid || meta.message_uuid;
      const extras = { toolCalls, messageUuid };

      if (meta.clarification_request || meta.needs_clarification) {
        if (!S().feedItems.some((f) => f.type === "clarification")) {
          sseEventHandlers.clarification_request(
            meta.clarification_request || { message: p.content },
          );
        }
      } else if (S().feedItems.some((f) => f.type === "clarification")) {
        sseState.finalized = true;
      } else {
        const streamingContent = S().streamingContent;
        const normalizedContent = normalizeAssistantContent(p.content || streamingContent || "");
        if (streamingContent) {
          S().finalizeStreaming(normalizedContent || streamingContent, extras);
        } else if (p.content) {
          S().addFeedItem({
            type: "chat_agent",
            data: { content: normalizedContent, ...extras },
          });
        }
        sseState.finalized = true;
      }
      return "continue";
    }

    if (p.replay && p.delta) {
      sseState.finalized = true;
      const streamingContent = S().streamingContent;
      const normalizedReplay = normalizeAssistantContent(p.delta);
      if (streamingContent) S().finalizeStreaming(normalizedReplay);
      else S().addFeedItem({ type: "chat_agent", data: { content: normalizedReplay } });
      return "continue";
    }

    if (p.type === "text_delta" || p.type === "TEXT_MESSAGE_CONTENT" || p.delta != null) {
      if (!sseState.runOrgId && !sseState.runUserId) return "continue";
      if (p.org_id && p.org_id !== sseState.runOrgId) return "continue";
      if (p.user_id && p.user_id !== sseState.runUserId) return "continue";
      if (!p.replay) {
        let delta = p.delta || p.content || "";
        delta = (sseState.heldPartialTag || "") + delta;
        sseState.heldPartialTag = "";

        if (!S().streamingContent) {
          delta = delta.replace(
            /^[\s]*(?:<\/tool_calls>|<\/function_calls>|<\/tool_function_calls>|<\/antml_\w+>|<\/invoke>)+[\s]*/g,
            "",
          );
        }

        delta = delta.replace(
          /<(?!(?:br|hr|u|b|i|em|strong|code|sup|sub|kbd|mark|small)\b)([A-Za-z][\w-]*)(?:\s[^>]*)?>[\s\S]*?<\/\1>/g,
          "",
        );
        delta = delta.replace(
          /<\/?(?!(?:br|hr|u|b|i|em|strong|code|sup|sub|kbd|mark|small)\b)[A-Za-z][\w-]*(?:\s[^>]*)?\/?>/g,
          "",
        );

        const pendingMatch = delta.match(/<[A-Za-z][\w-]*(?:\s[^>]*)?$/);
        if (pendingMatch && pendingMatch.index != null) {
          sseState.heldPartialTag = delta.slice(pendingMatch.index);
          delta = delta.slice(0, pendingMatch.index);
        }

        const sofar = (S().streamingContent || "") + delta;
        const hasPrefetchMarker =
          sofar.includes("## Prefetched Code") ||
          sofar.includes("[DEPS_OFFER]") ||
          sofar.includes("=== Output ===");
        delta = normalizeStreamingDelta(delta);
        if (delta && !hasPrefetchMarker) S().appendStreamingDelta(delta);
      }
      return "continue";
    }

    const normalizedType = normalizeSseEventType(p?.type || eventType);
    sseEventHandlers[normalizedType]?.(p);
    if (normalizedType === "run_end") {
      return "close";
    }
  } catch (err) {
    if (process.env.NODE_ENV === "development") {
      console.debug("[SSE] parse error:", err, eventData.slice(0, 200));
    }
  }

  return "continue";
}
